// Comprehensive worldwide country and currency directory for travel and offline expense tracking.

export interface CountryInfo {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  currency: string; // ISO 4217
  currencyName: string;
  flag: string;
  aliases?: string[];
}

export const COUNTRIES: CountryInfo[] = [
  // Southeast Asia & East Asia
  { code: "MY", name: "Malaysia", currency: "MYR", currencyName: "Malaysian Ringgit", flag: "🇲🇾", aliases: ["kuala lumpur", "penang", "langkawi", "malay"] },
  { code: "ID", name: "Indonesia", currency: "IDR", currencyName: "Indonesian Rupiah", flag: "🇮🇩", aliases: ["bali", "jakarta", "lombok", "ubud", "indo"] },
  { code: "TH", name: "Thailand", currency: "THB", currencyName: "Thai Baht", flag: "🇹🇭", aliases: ["bangkok", "phuket", "chiang mai", "krabi", "pattaya", "thai"] },
  { code: "SG", name: "Singapore", currency: "SGD", currencyName: "Singapore Dollar", flag: "🇸🇬", aliases: ["changi", "singa"] },
  { code: "JP", name: "Japan", currency: "JPY", currencyName: "Japanese Yen", flag: "🇯🇵", aliases: ["tokyo", "osaka", "kyoto", "hokkaido"] },
  { code: "KR", name: "South Korea", currency: "KRW", currencyName: "South Korean Won", flag: "🇰🇷", aliases: ["korea", "seoul", "busan", "jeju"] },
  { code: "VN", name: "Vietnam", currency: "VND", currencyName: "Vietnamese Dong", flag: "🇻🇳", aliases: ["hanoi", "ho chi minh", "saigon", "da nang"] },
  { code: "PH", name: "Philippines", currency: "PHP", currencyName: "Philippine Peso", flag: "🇵🇭", aliases: ["manila", "cebu", "boracay", "palawan"] },
  { code: "CN", name: "China", currency: "CNY", currencyName: "Chinese Yuan", flag: "🇨🇳", aliases: ["beijing", "shanghai", "guangzhou", "shenzhen"] },
  { code: "HK", name: "Hong Kong", currency: "HKD", currencyName: "Hong Kong Dollar", flag: "🇭🇰" },
  { code: "TW", name: "Taiwan", currency: "TWD", currencyName: "New Taiwan Dollar", flag: "🇹🇼", aliases: ["taipei"] },
  { code: "KH", name: "Cambodia", currency: "KHR", currencyName: "Cambodian Riel", flag: "🇰🇭", aliases: ["siem reap", "phnom penh"] },
  { code: "LA", name: "Laos", currency: "LAK", currencyName: "Lao Kip", flag: "🇱🇦", aliases: ["vientiane", "luang prabang"] },
  { code: "MM", name: "Myanmar", currency: "MMK", currencyName: "Myanmar Kyat", flag: "🇲🇲", aliases: ["burma", "yangon"] },

  // South Asia
  { code: "MV", name: "Maldives", currency: "MVR", currencyName: "Maldivian Rufiyaa", flag: "🇲🇻", aliases: ["male", "malé", "hulhumale", "maafushi"] },
  { code: "LK", name: "Sri Lanka", currency: "LKR", currencyName: "Sri Lankan Rupee", flag: "🇱🇰", aliases: ["colombo", "kandy", "galle"] },
  { code: "IN", name: "India", currency: "INR", currencyName: "Indian Rupee", flag: "🇮🇳", aliases: ["delhi", "mumbai", "bangalore", "kerala", "goa", "chennai"] },
  { code: "NP", name: "Nepal", currency: "NPR", currencyName: "Nepalese Rupee", flag: "🇳🇵", aliases: ["kathmandu", "pokhara"] },
  { code: "BT", name: "Bhutan", currency: "BTN", currencyName: "Bhutanese Ngultrum", flag: "🇧🇹", aliases: ["thimphu"] },
  { code: "BD", name: "Bangladesh", currency: "BDT", currencyName: "Bangladeshi Taka", flag: "🇧🇩", aliases: ["dhaka"] },
  { code: "PK", name: "Pakistan", currency: "PKR", currencyName: "Pakistani Rupee", flag: "🇵🇰", aliases: ["karachi", "lahore", "islamabad"] },

  // Middle East & North Africa
  { code: "AE", name: "United Arab Emirates", currency: "AED", currencyName: "UAE Dirham", flag: "🇦🇪", aliases: ["uae", "dubai", "abu dhabi", "sharjah"] },
  { code: "SA", name: "Saudi Arabia", currency: "SAR", currencyName: "Saudi Riyal", flag: "🇸🇦", aliases: ["saudi", "makkah", "mecca", "medina", "madinah", "riyadh", "jeddah"] },
  { code: "QA", name: "Qatar", currency: "QAR", currencyName: "Qatari Riyal", flag: "🇶🇦", aliases: ["doha"] },
  { code: "OM", name: "Oman", currency: "OMR", currencyName: "Omani Rial", flag: "🇴🇲", aliases: ["muscat", "salalah"] },
  { code: "BH", name: "Bahrain", currency: "BHD", currencyName: "Bahraini Dinar", flag: "🇧🇭", aliases: ["manama"] },
  { code: "KW", name: "Kuwait", currency: "KWD", currencyName: "Kuwaiti Dinar", flag: "🇰🇼" },
  { code: "JO", name: "Jordan", currency: "JOD", currencyName: "Jordanian Dinar", flag: "🇯🇴", aliases: ["amman", "petra"] },
  { code: "EG", name: "Egypt", currency: "EGP", currencyName: "Egyptian Pound", flag: "🇪🇬", aliases: ["cairo", "giza", "hurghada", "sharm el sheikh"] },
  { code: "MA", name: "Morocco", currency: "MAD", currencyName: "Moroccan Dirham", flag: "🇲🇦", aliases: ["marrakech", "casablanca", "rabat"] },
  { code: "TR", name: "Turkey", currency: "TRY", currencyName: "Turkish Lira", flag: "🇹🇷", aliases: ["türkiye", "turkiye", "istanbul", "antalya", "cappadocia"] },

  // Europe
  { code: "GB", name: "United Kingdom", currency: "GBP", currencyName: "British Pound", flag: "🇬🇧", aliases: ["uk", "britain", "england", "london", "scotland", "edinburgh"] },
  { code: "FR", name: "France", currency: "EUR", currencyName: "Euro", flag: "🇫🇷", aliases: ["paris", "nice", "lyon"] },
  { code: "DE", name: "Germany", currency: "EUR", currencyName: "Euro", flag: "🇩🇪", aliases: ["berlin", "munich", "frankfurt"] },
  { code: "IT", name: "Italy", currency: "EUR", currencyName: "Euro", flag: "🇮🇹", aliases: ["rome", "milan", "venice", "florence"] },
  { code: "ES", name: "Spain", currency: "EUR", currencyName: "Euro", flag: "🇪🇸", aliases: ["barcelona", "madrid", "mallorca", "ibiza"] },
  { code: "PT", name: "Portugal", currency: "EUR", currencyName: "Euro", flag: "🇵🇹", aliases: ["lisbon", "porto"] },
  { code: "NL", name: "Netherlands", currency: "EUR", currencyName: "Euro", flag: "🇳🇱", aliases: ["amsterdam", "holland", "rotterdam"] },
  { code: "CH", name: "Switzerland", currency: "CHF", currencyName: "Swiss Franc", flag: "🇨🇭", aliases: ["swiss", "zurich", "geneva", "lucerne"] },
  { code: "AT", name: "Austria", currency: "EUR", currencyName: "Euro", flag: "🇦🇹", aliases: ["vienna", "salzburg"] },
  { code: "BE", name: "Belgium", currency: "EUR", currencyName: "Euro", flag: "🇧🇪", aliases: ["brussels", "bruges"] },
  { code: "GR", name: "Greece", currency: "EUR", currencyName: "Euro", flag: "🇬🇷", aliases: ["athens", "santorini", "mykonos"] },
  { code: "IE", name: "Ireland", currency: "EUR", currencyName: "Euro", flag: "🇮🇪", aliases: ["dublin"] },
  { code: "SE", name: "Sweden", currency: "SEK", currencyName: "Swedish Krona", flag: "🇸🇪", aliases: ["stockholm"] },
  { code: "NO", name: "Norway", currency: "NOK", currencyName: "Norwegian Krone", flag: "🇳🇴", aliases: ["oslo", "bergen"] },
  { code: "DK", name: "Denmark", currency: "DKK", currencyName: "Danish Krone", flag: "🇩🇰", aliases: ["copenhagen"] },
  { code: "FI", name: "Finland", currency: "EUR", currencyName: "Euro", flag: "🇫🇮", aliases: ["helsinki"] },
  { code: "CZ", name: "Czech Republic", currency: "CZK", currencyName: "Czech Koruna", flag: "🇨🇿", aliases: ["prague", "czechia"] },
  { code: "HU", name: "Hungary", currency: "HUF", currencyName: "Hungarian Forint", flag: "🇭🇺", aliases: ["budapest"] },
  { code: "PL", name: "Poland", currency: "PLN", currencyName: "Polish Zloty", flag: "🇵🇱", aliases: ["warsaw", "krakow"] },
  { code: "HR", name: "Croatia", currency: "EUR", currencyName: "Euro", flag: "🇭🇷", aliases: ["dubrovnik", "split", "zagreb"] },
  { code: "IS", name: "Iceland", currency: "ISK", currencyName: "Icelandic Krona", flag: "🇮🇸", aliases: ["reykjavik"] },
  { code: "GE", name: "Georgia", currency: "GEL", currencyName: "Georgian Lari", flag: "🇬🇪", aliases: ["tbilisi", "batumi"] },
  { code: "RO", name: "Romania", currency: "RON", currencyName: "Romanian Leu", flag: "🇷🇴", aliases: ["bucharest"] },
  { code: "BG", name: "Bulgaria", currency: "BGN", currencyName: "Bulgarian Lev", flag: "🇧🇬", aliases: ["sofia"] },

  // Americas
  { code: "US", name: "United States", currency: "USD", currencyName: "US Dollar", flag: "🇺🇸", aliases: ["usa", "america", "new york", "los angeles", "california", "florida", "hawaii"] },
  { code: "CA", name: "Canada", currency: "CAD", currencyName: "Canadian Dollar", flag: "🇨🇦", aliases: ["toronto", "vancouver", "montreal"] },
  { code: "MX", name: "Mexico", currency: "MXN", currencyName: "Mexican Peso", flag: "🇲🇽", aliases: ["cancun", "mexico city"] },
  { code: "BR", name: "Brazil", currency: "BRL", currencyName: "Brazilian Real", flag: "🇧🇷", aliases: ["rio", "sao paulo"] },
  { code: "AR", name: "Argentina", currency: "ARS", currencyName: "Argentine Peso", flag: "🇦🇷", aliases: ["buenos aires"] },
  { code: "CL", name: "Chile", currency: "CLP", currencyName: "Chilean Peso", flag: "🇨🇱", aliases: ["santiago"] },
  { code: "CO", name: "Colombia", currency: "COP", currencyName: "Colombian Peso", flag: "🇨🇴", aliases: ["bogota", "medellin"] },
  { code: "PE", name: "Peru", currency: "PEN", currencyName: "Peruvian Sol", flag: "🇵🇪", aliases: ["lima", "cusco"] },
  { code: "CR", name: "Costa Rica", currency: "CRC", currencyName: "Costa Rican Colon", flag: "🇨🇷" },

  // Oceania
  { code: "AU", name: "Australia", currency: "AUD", currencyName: "Australian Dollar", flag: "🇦🇺", aliases: ["sydney", "melbourne", "brisbane", "perth", "aussie"] },
  { code: "NZ", name: "New Zealand", currency: "NZD", currencyName: "New Zealand Dollar", flag: "🇳🇿", aliases: ["auckland", "queenstown", "kiwi"] },
  { code: "FJ", name: "Fiji", currency: "FJD", currencyName: "Fijian Dollar", flag: "🇫🇯" },

  // Africa
  { code: "ZA", name: "South Africa", currency: "ZAR", currencyName: "South African Rand", flag: "🇿🇦", aliases: ["cape town", "johannesburg"] },
  { code: "KE", name: "Kenya", currency: "KES", currencyName: "Kenyan Shilling", flag: "🇰🇪", aliases: ["nairobi", "mombasa"] },
  { code: "TZ", name: "Tanzania", currency: "TZS", currencyName: "Tanzanian Shilling", flag: "🇹🇿", aliases: ["zanzibar", "kilimanjaro"] },
  { code: "MU", name: "Mauritius", currency: "MUR", currencyName: "Mauritian Rupee", flag: "🇲🇺" },
  { code: "SC", name: "Seychelles", currency: "SCR", currencyName: "Seychellois Rupee", flag: "🇸🇨" },
];

export const POPULAR_TRAVEL_CURRENCIES = [
  "USD", "EUR", "MYR", "THB", "IDR", "SGD", "GBP", "JPY", "AED", "SAR", "INR", "AUD", "CAD", "CHF", "TRY", "VND", "PHP", "LKR"
];

// Normalize query for search matching
function clean(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Looks up country info by name, alias, city, ISO code, or currency code. */
export function lookupCountry(query: string): CountryInfo | undefined {
  if (!query) return undefined;
  const q = clean(query);
  if (!q) return undefined;

  // 1. Direct country code match (e.g. "MY", "US")
  if (query.trim().length === 2) {
    const codeMatch = COUNTRIES.find((c) => c.code.toLowerCase() === query.trim().toLowerCase());
    if (codeMatch) return codeMatch;
  }

  // 2. Direct currency code match (e.g. "MYR", "THB", "IDR")
  if (query.trim().length === 3) {
    const currMatch = COUNTRIES.find((c) => c.currency.toLowerCase() === query.trim().toLowerCase());
    if (currMatch) return currMatch;
  }

  // 3. Name or alias match
  return COUNTRIES.find((c) => {
    if (clean(c.name) === q) return true;
    if (clean(c.code) === q) return true;
    if (clean(c.currency) === q) return true;
    if (c.aliases?.some((a) => clean(a) === q)) return true;
    return false;
  }) || COUNTRIES.find((c) => {
    // Partial substring fallback
    if (clean(c.name).includes(q) || q.includes(clean(c.name))) return true;
    if (c.aliases?.some((a) => clean(a).includes(q) || q.includes(clean(a)))) return true;
    return false;
  });
}

/** Infers the currency code for any destination or country string. */
export function inferCurrencyForDestination(destination: string): string | undefined {
  if (!destination) return undefined;
  const trimmed = destination.trim();
  // If user entered a 3-letter currency code directly (e.g., "USD", "MYR", "CHF")
  if (trimmed.length === 3 && /^[A-Za-z]{3}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  const match = lookupCountry(destination);
  return match?.currency;
}

/** Returns the flag emoji for a currency or country. */
export function getCurrencyFlag(currencyOrCountry: string): string {
  if (!currencyOrCountry) return "🌍";
  const match = lookupCountry(currencyOrCountry);
  if (match) return match.flag;
  const byCurr = COUNTRIES.find((c) => c.currency.toUpperCase() === currencyOrCountry.toUpperCase());
  return byCurr?.flag || "🌍";
}

/** Returns the display name for a currency code. */
export function getCurrencyDisplayName(currencyCode: string): string {
  const match = COUNTRIES.find((c) => c.currency.toUpperCase() === currencyCode.toUpperCase());
  return match ? `${match.flag} ${match.currency} (${match.name})` : currencyCode;
}
